import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import NumericTextField from './NumericTextField';

/**
 * Reusable Flight Search Form Component
 * Used in both DealsPlan and FlightSearchResults for consistency
 */
function FlightSearchForm({ 
  initialOrigin = '',
  initialDestination = '',
  initialDepartDate = '',
  initialReturnDate = '',
  initialPassengers = 1,
  initialTripType = 'round_trip',
  onSearch,
  submitButtonText = 'Search Flights',
  compact = false
}) {
  const navigate = useNavigate();
  
  // Support multiple airports as comma-separated or array
  const [origin, setOrigin] = useState(initialOrigin);
  const [destination, setDestination] = useState(initialDestination);
  const [departDate, setDepartDate] = useState(initialDepartDate);
  const [returnDate, setReturnDate] = useState(initialReturnDate);
  const [passengers, setPassengers] = useState(initialPassengers);
  const [tripType, setTripType] = useState(initialTripType);
  
  const [originOptions, setOriginOptions] = useState([]);
  const [destinationOptions, setDestinationOptions] = useState([]);

  const handleOriginSearch = async (value) => {
    if (value && value.length >= 2) {
      const { searchAirports } = await import('../utils/airportDatabase');
      const results = searchAirports(value);
      setOriginOptions(results.map(r => r.label));
    }
  };

  const handleDestinationSearch = async (value) => {
    if (value && value.length >= 2) {
      const { searchAirports } = await import('../utils/airportDatabase');
      const results = searchAirports(value);
      setDestinationOptions(results.map(r => r.label));
    }
  };

  const handleSubmit = async () => {
    if (!origin || !destination || !departDate) {
      alert('Please fill in origin, destination, and departure date');
      return;
    }

    if (tripType === 'round_trip' && !returnDate) {
      alert('Please select a return date for round trip');
      return;
    }

    // origin and destination are already comma-separated IATA codes from onChange
    const originIATA = origin;
    const destinationIATA = destination;

    console.log(`🔍 Multi-airport search: ${originIATA} → ${destinationIATA}`);

    // If onSearch callback provided, call it
    if (onSearch) {
      onSearch({
        origin: originIATA,
        destination: destinationIATA,
        departDate,
        returnDate,
        passengers,
        tripType
      });
      return;
    }

    // Otherwise, navigate to search results
    const searchParams = new URLSearchParams({
      origin: originIATA,
      destination: destinationIATA,
      departDate,
      returnDate: returnDate || '',
      passengers: passengers.toString(),
      tripType
    });
    navigate(`/flights/search?${searchParams.toString()}`);
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Trip Type Toggle */}
        {!compact && (
          <Grid item xs={12}>
            <ToggleButtonGroup
              value={tripType}
              exclusive
              onChange={(e, newType) => newType && setTripType(newType)}
              size="small"
            >
              <ToggleButton value="round_trip">Round Trip</ToggleButton>
              <ToggleButton value="one_way">One Way</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        )}

        {/* Origin */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            multiple
            freeSolo
            options={originOptions}
            value={typeof origin === 'string' ? origin.split(',').map(s => s.trim()).filter(Boolean) : (Array.isArray(origin) ? origin : [])}
            onInputChange={async (event, value) => {
              if (value && value.length >= 2) {
                await handleOriginSearch(value);
              }
            }}
            onChange={async (event, value, reason) => {
              // value is array of selected items
              if (Array.isArray(value)) {
                const { convertToIATA } = await import('../utils/airportDatabase');
                // Convert each to IATA and join with commas
                const iatas = value.map(v => {
                  // If it's already an IATA code format, keep it, otherwise convert
                  const trimmed = v.trim().toUpperCase();
                  if (trimmed.length === 3 && /^[A-Z]{3}$/.test(trimmed)) {
                    return trimmed;
                  }
                  return convertToIATA(v);
                }).filter(Boolean);
                setOrigin(iatas.join(','));
              } else if (value === null || value.length === 0) {
                setOrigin('');
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="From (City or IATA)"
                size={compact ? 'small' : 'medium'}
                placeholder="e.g., SFO or add multiple"
                helperText={compact ? '' : 'Type and press Enter to add'}
                required
              />
            )}
            ChipProps={{
              size: 'small',
              color: 'primary',
              variant: 'outlined'
            }}
          />
        </Grid>

        {/* Destination */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            multiple
            freeSolo
            options={destinationOptions}
            value={typeof destination === 'string' ? destination.split(',').map(s => s.trim()).filter(Boolean) : (Array.isArray(destination) ? destination : [])}
            onInputChange={async (event, value) => {
              if (value && value.length >= 2) {
                await handleDestinationSearch(value);
              }
            }}
            onChange={async (event, value, reason) => {
              // value is array of selected items
              if (Array.isArray(value)) {
                const { convertToIATA } = await import('../utils/airportDatabase');
                // Convert each to IATA and join with commas
                const iatas = value.map(v => {
                  // If it's already an IATA code format, keep it, otherwise convert
                  const trimmed = v.trim().toUpperCase();
                  if (trimmed.length === 3 && /^[A-Z]{3}$/.test(trimmed)) {
                    return trimmed;
                  }
                  return convertToIATA(v);
                }).filter(Boolean);
                setDestination(iatas.join(','));
              } else if (value === null || value.length === 0) {
                setDestination('');
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="To (City or IATA)"
                size={compact ? 'small' : 'medium'}
                placeholder="e.g., LAX or add multiple"
                helperText={compact ? '' : 'Type and press Enter to add'}
                required
              />
            )}
            ChipProps={{
              size: 'small',
              color: 'primary',
              variant: 'outlined'
            }}
          />
        </Grid>

        {/* Departure Date */}
        <Grid item xs={12} sm={tripType === 'round_trip' ? 4 : 6}>
          <TextField
            fullWidth
            label="Departure"
            type="date"
            value={departDate}
            onChange={(e) => {
              const newDepartDate = e.target.value;
              setDepartDate(newDepartDate);
              
              // If return date is before new departure date, adjust it
              if (tripType === 'round_trip' && returnDate && newDepartDate > returnDate) {
                setReturnDate(newDepartDate);
              }
            }}
            size={compact ? 'small' : 'medium'}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: new Date().toISOString().split('T')[0] }}
            required
          />
        </Grid>

        {/* Return Date */}
        {tripType === 'round_trip' && (
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Return"
              type="date"
              value={returnDate}
              onChange={(e) => setReturnDate(e.target.value)}
              size={compact ? 'small' : 'medium'}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: departDate || new Date().toISOString().split('T')[0] }}
              required
            />
          </Grid>
        )}

        {/* Passengers */}
        <Grid item xs={12} sm={tripType === 'round_trip' ? 4 : 6}>
          <NumericTextField
            fullWidth
            label="Passengers"
            value={passengers}
            onValueChange={setPassengers}
            min={1}
            max={9}
            size={compact ? 'small' : 'medium'}
          />
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            size={compact ? 'medium' : 'large'}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              fontWeight: 600,
              py: compact ? 1.5 : 2,
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #63408b 100%)',
              },
            }}
          >
            {submitButtonText}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

export default FlightSearchForm;

